import { useState, useEffect, useRef } from "react";
import { celebratePerfect, celebrateGood, celebrateStandard } from "../lib/confetti";
import { T, ALL_LABELS } from "../lib/i18n";
import { scoreColor, friendlyError } from "../lib/helpers";
import { forgetGroup, dbLoadLiveSession, dbSaveAppeal, dbSaveBQ, dbLoadGroupFeedback, dbAnswerDelegation, dbLoadGroupResult, dbFinishGroup, getGroupRef, dbEnsureGroupRef } from "../lib/db";
import { openStudentResultSheet } from "../lib/export";
import { startPolling, useServerTrouble } from "../lib/net";
import { useVersionCheck } from "../lib/version";
import { useTabSleep, STUDENT_IDLE_SLEEP } from "../lib/sleep";
import { supabase } from "../supabaseClient";
import { Download, Scale, MessageCircleQuestion, ChevronRight, PartyPopper, Check, LogOut } from "lucide-react";
import { Dialog, AppHeader, QuestionImages, QuestionText, RichText, ServerTroubleBanner, SleepBanner, SessionClosed, ConfirmDialog } from "../components/common";

// ─────────────────────────────────────────
//  POST-SESSION MODAL
// ─────────────────────────────────────────
// Lärarens beslut om en appeal, som studenterna ser det. adjusted_score är
// frågans nya poäng vid godkännande och avdraget (negativt) vid avslag.
function appealDecisionText(a, lang) {
  const sv = lang === "sv";
  const q = a.questionIndex + 1;
  const pts = a.adjusted_score;
  if (a.status === "approved") {
    return sv
      ? `Er appeal på fråga ${q} är godkänd.${pts != null ? ` Frågan ger nu ${pts} poäng.` : ""}`
      : `Your appeal on question ${q} was approved.${pts != null ? ` The question now gives ${pts} points.` : ""}`;
  }
  return sv
    ? `Er appeal på fråga ${q} fick avslag.${pts != null && pts < 0 ? ` Avdrag: ${-pts} poäng.` : ""}`
    : `Your appeal on question ${q} was rejected.${pts != null && pts < 0 ? ` Deduction: ${-pts} points.` : ""}`;
}

function AppealStatusChip({ appeal, lang, G }) {
  const sv = lang === "sv";
  const s = appeal.status || "pending";
  const [label, color] = s === "approved" ? [sv ? "Godkänd" : "Approved", G.correct]
    : s === "rejected" ? [sv ? "Avslagen" : "Rejected", G.muted]
    : [sv ? "Väntar på beslut" : "Awaiting decision", G.muted];
  return <span style={{ fontSize: 11, fontWeight: 600, color, background: color + "18", borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>{label}</span>;
}

// Början av en fråga som en rad: i väljaren och i "Gäller fråga 3: …"
function questionSnippet(qd, max = 70) {
  const s = String(qd?.text || "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/[*_#>`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  return (sp > max * 0.6 ? cut.slice(0, sp) : cut) + "…";
}

// Den valda frågan, alltid synlig: text, bilder och alternativ med gruppens
// skrapordning och (för klara frågor) rätt svar. Långa frågor kortas till
// några rader med "Visa hela frågan". key={qi} hos anroparen nollställer
// det läget när gruppen byter fråga.
function QuestionPreviewBox({ qi, session, states, lang, G }) {
  const [full, setFull] = useState(false);
  const qd = session.questions?.[qi];
  if (!qd) return null;
  const sv = lang === "sv";
  const s = states[qi] || {};
  // Rätt svar bara för frågor gruppen gjort klart — från serverns rättning
  const correctIdx = s.done ? (s.correct ?? null) : null;
  const OPTS = ALL_LABELS.slice(0, session.numOptions || 4);
  const long = (qd.text || "").length > 220 || OPTS.some(l => (qd.options?.[l] || "").length > 90);
  const short = long && !full;
  return (
    <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "12px 14px", marginTop: 10 }}>
      <div aria-live="polite" style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: G.muted, marginBottom: 6 }}>
        {sv ? `Fråga ${qi + 1}` : `Question ${qi + 1}`}{s.done ? ` · ${s.score} p` : ""}
      </div>
      {qd.text && (
        <div style={short ? { maxHeight: "6.2em", overflow: "hidden", position: "relative" } : undefined}>
          <QuestionText text={qd.text} style={{ fontSize: 14, color: G.text, marginBottom: 8, fontWeight: 500, lineHeight: 1.55 }} />
          {/* Mjuk toning i stället för en rad som klipps mitt itu */}
          {short && <div aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "2.2em", background: `linear-gradient(to bottom, ${G.bg}00, ${G.bg})` }} />}
        </div>
      )}
      <QuestionImages images={qd.images} G={G} lang={lang} maxHeight={short ? 140 : 280} />
      {OPTS.map((lbl, oi) => {
        const scratchIdx = s.attempts?.indexOf(oi) ?? -1;
        const isCorrect = correctIdx === oi;
        return (
          <div key={lbl} style={{ display: "flex", gap: 8, alignItems: short ? "center" : "flex-start", marginBottom: 4, minWidth: 0 }}>
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: isCorrect ? G.correct : scratchIdx >= 0 ? G.gold : G.muted, fontWeight: 600, minWidth: 22, flexShrink: 0 }}>
              {lbl}{isCorrect ? " ✓" : ""}
            </span>
            <span style={{ fontSize: 13, color: isCorrect ? G.correct : G.text, flex: 1, minWidth: 0, lineHeight: 1.5, ...(short ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}) }}>
              {qd.options?.[lbl] || ""}
            </span>
            {scratchIdx >= 0 && !isCorrect && (
              <span style={{ fontSize: 10, color: G.gold, background: G.gold + "18", borderRadius: 999, padding: "1px 6px", flexShrink: 0 }}>
                {sv ? `${scratchIdx + 1}:a försök` : `try ${scratchIdx + 1}`}
              </span>
            )}
          </div>
        );
      })}
      {qd.rationale && session.showRationale && !short && <div style={{ fontSize: 13, color: G.goldDim || G.muted, marginTop: 8, fontStyle: "italic" }}>💡 <RichText text={qd.rationale} /></div>}
      {long && (
        <button onClick={() => setFull(f => !f)} aria-expanded={full}
          style={{ background: "none", border: "none", color: G.gold, fontSize: 13, fontWeight: 600, padding: "6px 0 0", cursor: "pointer" }}>
          {full ? (sv ? "Visa mindre" : "Show less") : (sv ? "Visa hela frågan" : "Show the whole question")}
        </button>
      )}
    </div>
  );
}

// Frågeväljare + den valda frågan — samma i appeal- och bq-flikarna. En
// vanlig <select> (bra på iPad och med skärmläsare), där varje val visar
// frågans början så att man oftast känner igen den direkt. marker(qi) kan
// lägga till t.ex. "appeal skickad".
function QuestionPicker({ id, label, highlight = false, states, session, selectedQ, onSelect, marker, lang, G, css }) {
  return (
    <div style={highlight
      ? { background: G.gold + "10", border: `1.5px solid ${G.gold}55`, borderRadius: 12, padding: "14px 16px", marginBottom: 18 }
      : { marginBottom: 16 }}>
      <label htmlFor={id} style={{ ...css.label, ...(highlight ? { color: G.gold } : {}) }}>{label}</label>
      <select id={id} style={{ ...css.input, width: "100%" }} value={selectedQ} onChange={e => onSelect(parseInt(e.target.value, 10))}>
        {states.map((s, qi) => {
          const snip = questionSnippet(session.questions?.[qi]);
          const m = marker ? marker(qi) : "";
          return (
            <option key={qi} value={qi} style={{ background: G.surface }}>
              {`Q${qi + 1}${snip ? ` — ${snip}` : ""}${s.done ? ` (${s.score} p)` : ""}${m ? ` · ${m}` : ""}`}
            </option>
          );
        })}
      </select>
      <QuestionPreviewBox key={selectedQ} qi={selectedQ} session={session} states={states} lang={lang} G={G} />
    </div>
  );
}

// Visas ovanför skicka-knappen: vilken fråga inskicket gäller
function AppliesTo({ qi, session, lang, G }) {
  const snip = questionSnippet(session.questions?.[qi], 80);
  return (
    <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}>
      {lang === "sv" ? "Gäller " : "Concerns "}<strong style={{ color: G.text }}>{lang === "sv" ? `fråga ${qi + 1}` : `question ${qi + 1}`}</strong>{snip ? `: ${snip}` : ""}
    </div>
  );
}

function PostSessionModal({ session, groupName, states, delegations, bqAnswers = [], onClose, initialTab, initialQ = 0, lang, G, css, isLocked = false }) {
  const t = T[lang];
  const LABELS = ALL_LABELS.slice(0, session.numOptions || 4);
  const [tab, setTab] = useState(initialTab || (delegations.length > 0 ? "delegations" : "appeal"));
  const [selectedQ, setSelectedQ] = useState(initialQ);
  const [appealType, setAppealType] = useState("green");
  // Utkast per fråga och typ: byter gruppen fråga ligger texten kvar på den
  // fråga den skrevs för — den följer inte med (då kunde en appeal hamna på
  // fel fråga) och den försvinner inte heller.
  const [appealDrafts, setAppealDrafts] = useState({});
  const [appealSubmitted, setAppealSubmitted] = useState({});
  const [submittedAppeals, setSubmittedAppeals] = useState([]);
  const [expandedAppealKey, setExpandedAppealKey] = useState(null);
  const [expandedScratchQ, setExpandedScratchQ] = useState(null);
  // Burning questions: ett utkast per fråga, av samma skäl
  const [bqDrafts, setBqDrafts] = useState({});
  const [bqSubmitted, setBqSubmitted] = useState({});
  const [delegationAnswers, setDelegationAnswers] = useState({});
  const [delegationAnswerText, setDelegationAnswerText] = useState({});
  const [feedback, setFeedback] = useState("");

  const appealKey = `${selectedQ}_${appealType}`;
  const fields = appealDrafts[appealKey] || {};
  function setField(k, v) { setAppealDrafts(p => ({ ...p, [appealKey]: { ...(p[appealKey] || {}), [k]: v } })); }
  const bqDraft = bqDrafts[selectedQ] || {};
  const bqText = bqDraft.text || "";
  const bqTopics = bqDraft.topics || {}; // key -> bool (en eller flera val)
  const bqOtherText = bqDraft.other || "";
  const setBqDraft = (k, v) => setBqDrafts(p => {
    const cur = p[selectedQ] || {};
    return { ...p, [selectedQ]: { ...cur, [k]: typeof v === "function" ? v(cur[k] || {}) : v } };
  });
  const setBqText = v => setBqDraft("text", v);
  const setBqTopics = v => setBqDraft("topics", v);
  const setBqOtherText = v => setBqDraft("other", v);
  function selectQuestion(q) { setSelectedQ(q); setFeedback(""); }

  // Ett token-verifierat anrop hämtar gruppens appeals, burning questions och
  // delegationer. Tabellerna är inte längre anonymt läsbara, så inget behöver
  // filtreras klientside — servern lämnar bara ut vår egen grupps rader.
  async function refreshAppeals() {
    const fb = await dbLoadGroupFeedback(session.sessionId, groupName);
    if (!fb) return;
    setSubmittedAppeals(fb.appeals);
    const amap = {};
    fb.appeals.forEach(a => { amap[`${a.questionIndex}_${a.type}`] = true; });
    setAppealSubmitted(amap);
    const bmap = {};
    fb.burningQuestions.forEach(b => { bmap[b.questionIndex] = true; });
    setBqSubmitted(bmap);
  }

  useEffect(() => { refreshAppeals(); }, [session.sessionId, groupName]);

  function showLocked() {
    setFeedback(t.sessionLockedMsg); setTimeout(() => setFeedback(""), 5000);
  }

  async function submitAppeal() {
    if (isLocked) { showLocked(); return; }
    if (appealSubmitted[appealKey]) return;
    // Väljaren visar A som förval — se till att värdet följer med även om
    // gruppen inte rört den
    const payload = appealType === "yellow"
      ? { proposedAnswer: LABELS[0], ...fields }
      : fields;
    // Ett nätverksfel här var förut helt tyst — formuläret stod kvar och
    // gruppen visste inte om texten sparats. Säg det, och låt dem försöka igen.
    try {
      await dbSaveAppeal(session.sessionId, groupName, selectedQ, appealType, payload);
    } catch (e) {
      setFeedback(friendlyError(e, lang)); setTimeout(() => setFeedback(""), 5000);
      return;
    }
    setAppealSubmitted(p => ({ ...p, [appealKey]: true }));
    setFeedback(t.appealSubmitted); setTimeout(() => setFeedback(""), 2000);
    setAppealDrafts(p => { const n = { ...p }; delete n[appealKey]; return n; });
    refreshAppeals();
  }

  const BQ_TOPICS = [
    { key: "whyCorrect", label: t.bqOptWhyCorrect },
    { key: "whyOurs", label: t.bqOptWhyOurs },
    { key: "difference", label: t.bqOptDifference },
    { key: "concept", label: t.bqOptConcept },
    { key: "other", label: t.bqOptOther },
  ];
  const bqTopicsChosen = BQ_TOPICS.filter(o => bqTopics[o.key]);
  const bqComplete = bqTopicsChosen.length > 0 && bqText.trim().length > 0;

  async function submitBQ() {
    if (isLocked) { showLocked(); return; }
    if (bqSubmitted[selectedQ] || !bqComplete) return;
    // Lagras som läsbar text i befintliga text-kolumnen: "val1 · val2 — beskrivning"
    const topicLabels = bqTopicsChosen.map(o =>
      o.key === "other" && bqOtherText.trim() ? `${o.label}: ${bqOtherText.trim()}` : o.label);
    const text = `${topicLabels.join(" · ")} — ${bqText.trim()}`;
    try {
      await dbSaveBQ(session.sessionId, groupName, selectedQ, text);
    } catch (e) {
      setFeedback(friendlyError(e, lang)); setTimeout(() => setFeedback(""), 5000);
      return;
    }
    setBqSubmitted(p => ({ ...p, [selectedQ]: true }));
    setBqDrafts(p => { const n = { ...p }; delete n[selectedQ]; return n; });
    setFeedback(t.bqSubmitted); setTimeout(() => setFeedback(""), 2000);
  }

  async function submitDelegationAnswer(del) {
    if (isLocked) { showLocked(); return; }
    const text = delegationAnswerText[del._key];
    if (!text?.trim()) return;
    // dbAnswerDelegation kastar inte — den returnerar false vid fel
    const ok = await dbAnswerDelegation(session.sessionId, groupName, del._key, text);
    if (!ok) {
      setFeedback(lang === "sv" ? "Svaret kunde inte sparas — försök igen." : "The answer could not be saved — try again.");
      setTimeout(() => setFeedback(""), 5000);
      return;
    }
    setDelegationAnswers(p => ({ ...p, [del._key]: text }));
    setFeedback(t.delegationAnswerSubmitted); setTimeout(() => setFeedback(""), 2000);
  }

  const TabBtn = ({ id, label, color, badge }) => (
    <button onClick={() => { setTab(id); setFeedback(""); }}
      style={{ flex: 1, position: "relative", background: tab === id ? (color || G.gold) : "transparent", color: tab === id ? (color ? "#fff" : G.bg) : G.muted, border: `1px solid ${tab === id ? (color || G.gold) : G.border}`, borderRadius: 8, padding: "8px 10px", fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>
      {label}{badge > 0 && <span style={{ position: "absolute", top: -6, right: -6, background: G.red, color: "#fff", borderRadius: 999, fontSize: 10, padding: "1px 6px" }}>{badge}</span>}
    </button>
  );

  const appealedQs = new Set(submittedAppeals.map(a => a.questionIndex));
  const pickerProps = { states, session, selectedQ, onSelect: selectQuestion, lang, G, css };
  // "Skicka in en till": nästa fråga där gruppen inte fick full poäng och
  // inte redan överklagat, i stället för bara nästa i ordningen
  function nextAppealCandidate() {
    const n = states.length;
    for (let k = 1; k <= n; k++) {
      const q = (selectedQ + k) % n;
      const st = states[q];
      if (st?.done && st.score < 4 && !appealedQs.has(q) && q !== selectedQ) return q;
    }
    return n > 1 ? (selectedQ + 1) % n : selectedQ;
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <Dialog onClose={onClose} label={t.appealTitle} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 18, padding: "32px 28px", maxWidth: 580, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ ...css.heading, fontSize: 20 }}>{tab === "bq" ? t.burningQuestions : tab === "delegations" ? t.delegationReceived : t.appealTitle}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: G.muted, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 22, flexWrap: "wrap" }}>
          <TabBtn id="appeal" label={`🟢🟡 ${t.appeals}`} />
          <TabBtn id="bq" label={`🔥 ${t.burningQuestions}`} color={G.burning} />
          <TabBtn id="myscratch" label={t.myAnswers} color="#6366f1" />
          {delegations.length > 0 && <TabBtn id="delegations" label={`💬 ${t.delegationAnswers}`} color="#6366f1" badge={delegations.filter(d => !delegationAnswers[d._key] && !d.answer).length} />}
        </div>
        {/* Avslutad tRAT: säg det, i stället för att inskicket tyst inte går igenom */}
        {tab !== "myscratch" && isLocked && (
          <div role="status" style={{ background: G.red + "12", border: `1px solid ${G.red}44`, borderRadius: 12, padding: "12px 16px", marginBottom: 16, fontSize: 14, color: G.text, textAlign: "left" }}>
            <div style={{ fontWeight: 600, color: G.red, marginBottom: 2 }}>{t.sessionLocked}</div>
            {t.sessionLockedMsg}
          </div>
        )}
        {tab === "bq" && <QuestionPicker id="bq-question" label={t.iFATQuestion} marker={q => (bqSubmitted[q] ? (lang === "sv" ? "BQ skickad" : "BQ sent") : "")} {...pickerProps} />}
        {tab === "appeal" && (
          <>
            <p style={{ color: G.muted, fontSize: 14, lineHeight: 1.6, marginBottom: 18, background: G.surface2, borderRadius: 12, padding: "12px 16px" }}>{t.appealSub}</p>
            {submittedAppeals.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: G.muted, marginBottom: 8 }}>{t.submittedAppealsLabel}</div>
                <div style={{ border: `1px solid ${G.border}`, borderRadius: 10, overflow: "hidden" }}>
                  {submittedAppeals
                    .slice()
                    .sort((a, b) => a.questionIndex - b.questionIndex || (a.type === b.type ? 0 : a.type === "green" ? -1 : 1))
                    .map((a, idx, arr) => {
                      const isLast = idx === arr.length - 1;
                      const isExpanded = expandedAppealKey === a._key;
                      const color = a.type === "green" ? G.greenAppeal : G.yellowAppeal;
                      const typeLabel = a.type === "green" ? t.greenAppealShort : t.yellowAppealShort;
                      const f = a.fields || {};
                      return (
                        <div key={a._key} style={{ borderBottom: !isLast ? `1px solid ${G.border}` : "none", background: G.surface }}>
                          <button
                            onClick={() => setExpandedAppealKey(isExpanded ? null : a._key)}
                            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "transparent", border: "none", padding: "12px 14px", cursor: "pointer", textAlign: "left", color: G.text, fontFamily: "inherit" }}>
                            <span style={{ width: 9, height: 9, borderRadius: "50%", background: color, flexShrink: 0 }} />
                            <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>Q{a.questionIndex + 1} — {typeLabel}</span>
                            <AppealStatusChip appeal={a} lang={lang} G={G} />
                            <span style={{ fontSize: 11, color: G.muted }}>{isExpanded ? "▲" : "▼"}</span>
                          </button>
                          {isExpanded && (
                            <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                              {a.type === "green" ? (
                                <>
                                  {f.ambiguity && <div><div style={{ fontSize: 11, color: G.muted, marginBottom: 3 }}>{t.ambiguitySource}</div><div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{f.ambiguity}</div></div>}
                                  {f.impact && <div><div style={{ fontSize: 11, color: G.muted, marginBottom: 3 }}>{t.impactQuestion}</div><div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{f.impact}</div></div>}
                                  {f.reasoning && <div><div style={{ fontSize: 11, color: G.muted, marginBottom: 3 }}>{t.reasoningQuestion}</div><div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{f.reasoning}</div></div>}
                                  {f.rewrite && <div><div style={{ fontSize: 11, color: G.muted, marginBottom: 3 }}>{t.rewriteQuestion}</div><div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{f.rewrite}</div></div>}
                                </>
                              ) : (
                                <>
                                  {f.proposedAnswer && <div><div style={{ fontSize: 11, color: G.muted, marginBottom: 3 }}>{t.proposedAnswer}</div><div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{f.proposedAnswer}</div></div>}
                                  {f.disagree && <div><div style={{ fontSize: 11, color: G.muted, marginBottom: 3 }}>{t.whyDisagree}</div><div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{f.disagree}</div></div>}
                                  {f.reference && <div><div style={{ fontSize: 11, color: G.muted, marginBottom: 3 }}>{t.materialReference}</div><div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{f.reference}</div></div>}
                                  {f.whyBest && <div><div style={{ fontSize: 11, color: G.muted, marginBottom: 3 }}>{t.whyBest}</div><div style={{ fontSize: 13, color: G.text, lineHeight: 1.5 }}>{f.whyBest}</div></div>}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            {/* Först frågan (alltid synlig), sedan typ — typen beror på frågan */}
            <QuestionPicker id="appeal-question" label={t.appealSelectQuestion} highlight
              marker={q => (appealedQs.has(q) ? (lang === "sv" ? "appeal skickad" : "appeal sent") : "")} {...pickerProps} />
            <div role="group" aria-label={lang === "sv" ? "Typ av appeal" : "Type of appeal"} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
              {[{ key: "green", color: G.greenAppeal, label: `🟢 ${t.greenAppealShort}` }, { key: "yellow", color: G.yellowAppeal, label: `🟡 ${t.yellowAppealShort}` }].map(({ key, color, label }) => (
                <button key={key} type="button" aria-pressed={appealType === key} onClick={() => setAppealType(key)} style={{ textAlign: "left", background: appealType === key ? color + "18" : G.bg, border: `2px solid ${appealType === key ? color : G.border}`, borderRadius: 12, padding: 14, cursor: "pointer", fontFamily: "inherit" }}>
                  <div style={{ ...css.heading, fontSize: 14, color: appealType === key ? color : G.text }}>{label}</div>
                </button>
              ))}
            </div>

            {appealSubmitted[appealKey]
              ? <div style={{ background: G.correct + "12", border: `1px solid ${G.correct}44`, borderRadius: 12, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: G.correct, fontSize: 14, fontWeight: 600 }}>
                    <span>✓</span>
                    <span>{t.appealSubmittedConfirm}</span>
                  </div>
                  <button
                    onClick={() => selectQuestion(nextAppealCandidate())}
                    style={{ ...css.btnSecondary, alignSelf: "flex-start" }}>
                    {t.submitAnotherAppeal}
                  </button>
                </div>
              : appealType === "green"
                ? (() => {
                    // Tvetydighet i frågan — alla fyra fält är obligatoriska
                    const greenFields = [
                      { key: "ambiguity", label: t.ambiguitySource },
                      { key: "impact", label: t.impactQuestion },
                      { key: "reasoning", label: t.reasoningQuestion },
                      { key: "rewrite", label: t.rewriteQuestion },
                    ];
                    const greenComplete = greenFields.every(f => (fields[f.key] || "").trim());
                    return (
                      <>
                        {greenFields.map((f, i) => (
                          <div key={f.key} style={{ marginBottom: i === greenFields.length - 1 ? 18 : 14 }}>
                            <label style={{ ...css.label, color: G.greenAppeal }}>{f.label} *</label>
                            <textarea style={css.textarea} value={fields[f.key] || ""} onChange={e => setField(f.key, e.target.value)} />
                          </div>
                        ))}
                        <AppliesTo qi={selectedQ} session={session} lang={lang} G={G} />
                        <button style={{ ...css.btnPrimary, background: G.greenAppeal, opacity: greenComplete ? 1 : 0.45 }} disabled={!greenComplete} onClick={submitAppeal}>{lang === "sv" ? `Lämna in appeal för fråga ${selectedQ + 1}` : `Submit appeal for question ${selectedQ + 1}`}</button>
                        {!greenComplete && <div style={{ fontSize: 12, color: G.muted, marginTop: 8 }}>{lang === "sv" ? "Alla fält är obligatoriska." : "All fields are required."}</div>}
                      </>
                    );
                  })()
                : (() => {
                    // Innehållsproblem i frågan — alla fält är obligatoriska
                    const yellowFields = [
                      { key: "disagree", label: t.whyDisagree },
                      { key: "whyBest", label: t.whyBest },
                      { key: "reference", label: t.materialReference },
                    ];
                    const yellowComplete = yellowFields.every(f => (fields[f.key] || "").trim());
                    return (
                      <>
                        <div style={{ marginBottom: 14 }}>
                          <label style={{ ...css.label, color: G.yellowAppeal }}>{t.proposedAnswer} *</label>
                          <select style={{ ...css.input, width: "auto" }} value={fields.proposedAnswer || LABELS[0]} onChange={e => setField("proposedAnswer", e.target.value)}>
                            {LABELS.map(l => <option key={l} value={l} style={{ background: G.surface }}>{l}</option>)}
                          </select>
                        </div>
                        {yellowFields.map((f, i) => (
                          <div key={f.key} style={{ marginBottom: i === yellowFields.length - 1 ? 18 : 14 }}>
                            <label style={{ ...css.label, color: G.yellowAppeal }}>{f.label} *</label>
                            <textarea style={css.textarea} value={fields[f.key] || ""} onChange={e => setField(f.key, e.target.value)} />
                          </div>
                        ))}
                        <AppliesTo qi={selectedQ} session={session} lang={lang} G={G} />
                        <button style={{ ...css.btnPrimary, background: G.yellowAppeal, color: G.onGold, opacity: yellowComplete ? 1 : 0.45 }} disabled={!yellowComplete} onClick={submitAppeal}>{lang === "sv" ? `Lämna in appeal för fråga ${selectedQ + 1}` : `Submit appeal for question ${selectedQ + 1}`}</button>
                        {!yellowComplete && <div style={{ fontSize: 12, color: G.muted, marginTop: 8 }}>{lang === "sv" ? "Alla fält är obligatoriska." : "All fields are required."}</div>}
                      </>
                    );
                  })()
            }
          </>
        )}
        {tab === "bq" && (
          <>
            <p style={{ color: G.muted, fontSize: 14, lineHeight: 1.6, marginBottom: 18, background: G.surface2, borderRadius: 12, padding: "12px 16px" }}>{t.burningQuestionsDesc}</p>
            {bqSubmitted[selectedQ]
              ? <div role="status" style={{ background: G.correct + "12", border: `1px solid ${G.correct}44`, borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 8, color: G.correct, fontSize: 14, fontWeight: 600, lineHeight: 1.5 }}>
                  <span aria-hidden="true">✓</span><span>{t.bqAlready}</span>
                </div>
              : <>
                  {/* Kryssval — en eller flera */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                    {BQ_TOPICS.map(o => (
                      <label key={o.key} style={{ display: "flex", alignItems: "center", gap: 10, background: bqTopics[o.key] ? G.burning + "12" : G.bg, border: `1.5px solid ${bqTopics[o.key] ? G.burning + "66" : G.border}`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", fontSize: 14, color: G.text, transition: "all 0.15s" }}>
                        <input
                          type="checkbox"
                          checked={!!bqTopics[o.key]}
                          onChange={e => setBqTopics(p => ({ ...p, [o.key]: e.target.checked }))}
                          style={{ width: 16, height: 16, accentColor: G.burning, flexShrink: 0 }}
                        />
                        <span style={{ flex: o.key === "other" ? "0 0 auto" : 1 }}>{o.label}{o.key === "other" ? ":" : ""}</span>
                        {o.key === "other" && (
                          <input
                            type="text"
                            value={bqOtherText}
                            onChange={e => { setBqOtherText(e.target.value); if (e.target.value.trim()) setBqTopics(p => ({ ...p, other: true })); }}
                            onClick={e => e.preventDefault()}
                            style={{ ...css.input, flex: 1, padding: "6px 10px", fontSize: 14 }}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ ...css.label, color: G.burning }}>{t.bqShortDesc} — Q{selectedQ + 1} *</label>
                    <textarea style={{ ...css.textarea, minHeight: 100 }} placeholder={t.burningQPlaceholder} value={bqText} onChange={e => setBqText(e.target.value)} />
                  </div>
                  <AppliesTo qi={selectedQ} session={session} lang={lang} G={G} />
                  <button style={{ ...css.btnPrimary, background: G.burning, opacity: bqComplete ? 1 : 0.45 }} disabled={!bqComplete} onClick={submitBQ}>{lang === "sv" ? `Skicka in burning question för fråga ${selectedQ + 1}` : `Submit burning question for question ${selectedQ + 1}`}</button>
                  {!bqComplete && <div style={{ fontSize: 12, color: G.muted, marginTop: 8 }}>{t.bqRequired}</div>}
                </>
            }
            {/* Show delegation answers for this group's BQs on the selected question */}
            {(() => {
              const answersForQ = bqAnswers.filter(a => a.iFATQuestion === selectedQ + 1);
              if (answersForQ.length === 0) return null;
              return answersForQ.map((a, i) => (
                <div key={i} style={{ marginTop: 14, background: a.answer ? G.correct + "0c" : G.surface2, border: `1px solid ${a.answer ? G.correct + "33" : G.border}`, borderRadius: 12, padding: "14px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 14 }}>{a.answer ? "✅" : "⏳"}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: a.answer ? G.correct : G.muted }}>
                      {a.answer ? `${t.bqAnsweredBy} ${a.toGroup}` : `${t.bqAwaitingAnswer} ${a.toGroup}…`}
                    </span>
                  </div>
                  {a.answer && <div style={{ fontSize: 14, color: G.text, lineHeight: 1.7, background: G.surface, borderRadius: 8, padding: "10px 14px" }}>{a.answer}</div>}
                </div>
              ));
            })()}
          </>
        )}
        {tab === "myscratch" && (
          <div>
            {states.map((s, qi) => {
              const OPTS = ALL_LABELS.slice(0, session.numOptions || 4);
              // Only revealed for finished questions (server-scored state)
              const correctIdx = s.done ? (s.correct ?? null) : null;
              const qd = session.questions?.[qi];
              const isExpanded = expandedScratchQ === qi;
              const canExpand = !!qd;
              return (
                <div key={qi} style={{ background: G.bg, border: `1px solid ${s.score === 4 ? G.correct + "44" : s.score > 0 ? G.gold + "33" : s.done ? G.red + "33" : G.border}`, borderRadius: 12, marginBottom: 8, overflow: "hidden" }}>
                  <div
                    onClick={canExpand ? () => setExpandedScratchQ(isExpanded ? null : qi) : undefined}
                    role={canExpand ? "button" : undefined}
                    tabIndex={canExpand ? 0 : undefined}
                    aria-expanded={canExpand ? isExpanded : undefined}
                    onKeyDown={canExpand ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpandedScratchQ(isExpanded ? null : qi); } } : undefined}
                    style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, cursor: canExpand ? "pointer" : "default" }}>
                    <div style={{ minWidth: 44, textAlign: "center" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: G.text }}>Q{qi + 1}</div>
                      <div style={{ fontSize: 12, color: s.score === 4 ? G.correct : s.score > 0 ? G.gold : s.done ? G.red : G.muted }}>
                        {s.done ? `${s.score} p` : "—"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", flex: 1, alignItems: "center" }}>
                      {s.attempts.length > 0 ? s.attempts.map((optIdx, i) => {
                        const isCorrect = optIdx === correctIdx;
                        return (
                          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 700, background: isCorrect ? G.correct + "22" : G.gold + "15", color: isCorrect ? G.correct : G.gold, border: `1px solid ${isCorrect ? G.correct : G.gold}44`, borderRadius: 6, padding: "3px 9px" }}>
                              {OPTS[optIdx]}{isCorrect ? " ✓" : ""}
                            </span>
                            {i < s.attempts.length - 1 && <span style={{ color: G.muted, fontSize: 12 }}>→</span>}
                          </span>
                        );
                      }) : <span style={{ color: G.muted, fontSize: 13 }}>{lang === 'sv' ? 'Ej påbörjad' : 'Not started'}</span>}
                    </div>
                    {s.done && s.score === 0 && correctIdx != null && (
                      <div style={{ fontSize: 12, color: G.correct, whiteSpace: "nowrap", background: G.correct + "18", borderRadius: 6, padding: "3px 8px" }}>✓ {OPTS[correctIdx]}</div>
                    )}
                    {canExpand && <span style={{ fontSize: 11, color: G.muted, marginLeft: 4 }}>{isExpanded ? "▲" : "▼"}</span>}
                  </div>
                  {isExpanded && qd && (
                    <div style={{ borderTop: `1px solid ${G.border}`, background: G.surface, padding: "14px 16px" }}>
                      {qd.text && <QuestionText text={qd.text} style={{ fontSize: 14, color: G.text, marginBottom: 10, fontWeight: 500, lineHeight: 1.5 }} />}
                      <QuestionImages images={qd.images} G={G} lang={lang} maxHeight={280} />
                      {OPTS.map((lbl, oi) => {
                        const isCorrect = correctIdx === oi;
                        const scratchIdx = s.attempts?.indexOf(oi);
                        return (
                          <div key={lbl} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: isCorrect ? G.correct : scratchIdx >= 0 ? G.gold : G.muted, fontWeight: 600, minWidth: 18 }}>
                              {lbl}{isCorrect ? " ✓" : ""}
                            </span>
                            <span style={{ fontSize: 13, color: isCorrect ? G.correct : G.text, flex: 1, lineHeight: 1.5 }}>{qd.options?.[lbl] || ""}</span>
                            {scratchIdx >= 0 && !isCorrect && (
                              <span style={{ fontSize: 10, color: G.gold, background: G.gold + "18", borderRadius: 999, padding: "1px 6px", flexShrink: 0 }}>
                                {lang === 'sv' ? `${scratchIdx + 1}:a försök` : `try ${scratchIdx + 1}`}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {qd.rationale && session.showRationale && <div style={{ fontSize: 13, color: G.goldDim || G.muted, marginTop: 10, fontStyle: "italic", lineHeight: 1.5 }}>💡 <RichText text={qd.rationale} /></div>}
                      {/* Genväg: "den här frågan ser konstig ut" → appeal eller BQ på just den */}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                        <button type="button" onClick={() => { selectQuestion(qi); setTab("appeal"); }}
                          style={{ background: "none", border: `1px solid ${G.greenAppeal}66`, borderRadius: 8, color: G.greenAppeal, fontSize: 13, fontWeight: 600, padding: "6px 12px", cursor: "pointer" }}>
                          {lang === "sv" ? "Gör appeal" : "Appeal"}
                        </button>
                        <button type="button" onClick={() => { selectQuestion(qi); setTab("bq"); }}
                          style={{ background: "none", border: `1px solid ${G.burning}66`, borderRadius: 8, color: G.burning, fontSize: 13, fontWeight: 600, padding: "6px 12px", cursor: "pointer" }}>
                          {lang === "sv" ? "Burning question" : "Burning question"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {tab === "delegations" && delegations.map((del, i) => {
          const answered = delegationAnswers[del._key] || del.answer;
          return (
            <div key={i} style={{ background: G.bg, border: `2px solid #6366f144`, borderRadius: 12, padding: "18px 20px", marginBottom: 14 }}>
              <div style={{ ...css.label, color: "#818cf8", marginBottom: 8 }}>💬 Q{del.iFATQuestion}</div>
              <div style={{ fontSize: 14, color: G.text, lineHeight: 1.7, marginBottom: 16, background: G.surface, borderRadius: 12, padding: "12px 16px" }}>{del.text}</div>
              {answered
                ? <div style={{ background: G.accent + "18", border: `1px solid ${G.accent}44`, borderRadius: 12, padding: "10px 14px", color: G.accent, fontSize: 14 }}>{t.delegationAnswerSubmitted}</div>
                : <><label style={{ ...css.label, color: "#818cf8" }}>{t.yourAnswer}</label><textarea style={{ ...css.textarea, marginBottom: 10 }} placeholder={t.delegationAnswerPlaceholder} value={delegationAnswerText[del._key] || ""} onChange={e => setDelegationAnswerText(p => ({ ...p, [del._key]: e.target.value }))} /><button style={{ ...css.btnPrimary, background: "#6366f1" }} onClick={() => submitDelegationAnswer(del)}>{t.submitDelegationAnswer}</button></>
              }
            </div>
          );
        })}
        {feedback && <div style={{ marginTop: 14, background: G.accent + "18", border: `1px solid ${G.accent}44`, borderRadius: 12, padding: "10px 14px", color: G.accent, textAlign: "center", fontSize: 14 }}>{feedback}</div>}
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────
//  GROUP RESULTS
// ─────────────────────────────────────────
// Poäng per försök (4/2/1/0) → stapelns delar på resultatsidan
const ATTEMPT_BARS = [
  { pts: 4, color: G => G.correct, label: lang => lang === "sv" ? "på första" : "on 1st try" },
  { pts: 2, color: G => G.correct + "88", label: lang => lang === "sv" ? "på andra" : "on 2nd try" },
  { pts: 1, color: G => G.gold, label: lang => lang === "sv" ? "på tredje" : "on 3rd try" },
  { pts: 0, color: G => G.muted + "88", label: lang => lang === "sv" ? "på fjärde / miss" : "on 4th / miss" },
];

const footerBtn = (G, color) => ({
  display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none",
  padding: "4px 0", color, fontSize: 14, fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
});

function GroupResults({ states: initialStates, session, groupName, onBack, onHome, onExit, onHelp, lang, setLang, theme, setTheme, G, css }) {
  // Det läge vi fick med från spelvyn är bara en startpunkt. Servern är
  // sanningen: hämta gruppens scratch_state direkt, så att en omladdning, en
  // djuplänk eller ett auto-avslut som hann före serverläget ändå visar rätt
  // poäng och historik.
  const [states, setStates] = useState(initialStates);
  // Spelvyn öppnar resultatsidan även om finish_group misslyckades (hellre
  // det än att låsa ute gruppen vid dåligt wifi). Då står gruppen kvar som
  // pågående på dashboarden. Gör om avslutet härifrån, och säg till om det
  // inte heller går.
  const [finishWarning, setFinishWarning] = useState(false);
  // Gruppens poäng från servern. Hämtas vid inladdning och igen när läraren
  // ändrat något (godkänd appeal, justerad poäng), så att studenterna inte
  // behöver ladda om sidan för att se den nya poängen.
  const applyScores = saved => {
    const st = saved?.scratchState;
    if (!Array.isArray(st) || st.length === 0) return;
    setStates(Array.from({ length: session.numQ }, (_, qi) => {
      const q = st[qi] || {};
      return { attempts: Array.isArray(q.attempts) ? q.attempts : [], done: !!q.done, score: q.score ?? null, correct: q.correct };
    }));
  };
  const applyScoresRef = useRef(applyScores);
  applyScoresRef.current = applyScores;
  useEffect(() => {
    let cancelled = false;
    dbLoadGroupResult(session.sessionId, groupName).then(async saved => {
      if (!cancelled && saved?.inProgress === true) {
        try { await dbFinishGroup(session.sessionId, groupName); }
        catch (e) { if (!cancelled) setFinishWarning(true); }
      }
      if (!cancelled) applyScores(saved);
    }).catch(() => { /* behåll det medskickade läget */ });
    return () => { cancelled = true; };
  }, [session.sessionId, session.numQ, groupName]);
  const [showModal, setShowModal] = useState(null);
  const [delegations, setDelegations] = useState([]);
  const [bqAnswers, setBqAnswers] = useState([]);
  // Beslut om appeals som kommit medan sidan varit öppen (visas tills de stängs)
  const [decisionNotices, setDecisionNotices] = useState([]);
  const knownDecisionsRef = useRef(null);
  // Svar från en annan grupp på en burning question vi skickat (delegerad av
  // läraren), som kommit medan sidan varit öppen
  const [answerNotices, setAnswerNotices] = useState([]);
  const knownAnswersRef = useRef(null);
  const [modalQ, setModalQ] = useState(0);
  const [isLocked, setIsLocked] = useState(session.sessionLocked || false);
  // Polled session copy in state — never mutate the session prop
  const [liveSession, setLiveSession] = useState(session);
  const t = T[lang];
  const total = states.reduce((s, q) => s + (q.score ?? 0), 0);
  const max = session.numQ * 4;
  const counts = { 4: 0, 2: 0, 1: 0, 0: 0 };
  states.forEach(q => { if (q.score !== null) counts[q.score] = (counts[q.score] || 0) + 1; });
  const doneCount = states.filter(q => q.done).length;
  // Feedback (delegerade frågor, svar, avgjorda appeals) kommer direkt: när
  // något ändras för gruppen sätter databasen feedback_at på gruppens rad i
  // group_progress, och den här sidan får en realtidshändelse och hämtar.
  // Utan realtid frågar den var 5:e sekund; med realtid bara var 30:e som
  // reserv. tRAT:ens status (lås, återkoppling) hämtas var 10:e sekund.
  // En flik som somnat (se useTabSleep) hämtar inget och stänger realtiden;
  // när den vaknar körs effekten igen och hämtar direkt.
  const liveSessionRef = useRef(liveSession);
  liveSessionRef.current = liveSession;
  const { asleep } = useTabSleep({ idleAfter: STUDENT_IDLE_SLEEP });
  useEffect(() => {
    if (asleep) return undefined;
    let subscribed = false;
    let cancelled = false;
    let channel = null;
    // Realtid räknas först när kanalen är uppe — och den öppnas bara med
    // gruppens ref, filtrerad på den, så att vi får vår egen grupps händelser
    // och inte alla gruppers i tRAT:en.
    const realtimeOk = () => subscribed;
    const loadScores = () => dbLoadGroupResult(session.sessionId, groupName)
      .then(saved => { if (!cancelled) applyScoresRef.current(saved); }).catch(() => {});
    const loadFeedback = async () => {
      const fb = await dbLoadGroupFeedback(session.sessionId, groupName);
      if (!fb) return false; // behåll det vi har; hämtloopen glesar ut
      if (cancelled) return true;
      setDelegations(fb.delegationsIn);
      setBqAnswers(fb.delegationsOut);
      // Nya beslut om appeals: visa dem och hämta den nya poängen. Beslut som
      // fanns redan när sidan öppnades syns i listan, men annonseras inte.
      const sig = a => `${a.status || "pending"}|${a.adjusted_score ?? ""}`;
      const known = knownDecisionsRef.current;
      if (known) {
        const fresh = fb.appeals.filter(a => a.status && a.status !== "pending" && known.get(a._key) !== sig(a));
        if (fresh.length) {
          setDecisionNotices(p => [...p.filter(x => !fresh.some(f => f._key === x._key)), ...fresh]);
          loadScores();
        }
      }
      knownDecisionsRef.current = new Map(fb.appeals.map(a => [a._key, sig(a)]));
      const knownAns = knownAnswersRef.current;
      if (knownAns) {
        const fresh = fb.delegationsOut.filter(d => d.answer && !knownAns.has(d._key));
        if (fresh.length) setAnswerNotices(p => [...p.filter(x => !fresh.some(f => f._key === x._key)), ...fresh]);
      }
      knownAnswersRef.current = new Set(fb.delegationsOut.filter(d => d.answer).map(d => d._key));
      return true;
    };
    const loadStatus = async () => {
      const update = await dbLoadLiveSession(session.sessionId, { hadRationale: liveSessionRef.current.showRationale });
      if (!update) return false;
      const next = update(liveSessionRef.current);
      setLiveSession(next);
      setIsLocked(next.sessionLocked);
      return true;
    };
    loadFeedback().catch(() => {});
    loadStatus().catch(() => {});
    const stopFeedback = startPolling(loadFeedback, { interval: () => (realtimeOk() ? 30000 : 5000) });
    const stopStatus = startPolling(loadStatus, { interval: 10000 });

    const openChannel = ref => {
      if (cancelled || !ref) return;
      channel = supabase.channel(`results-${session.sessionId}-${ref}`)
        .on("postgres_changes", {
          event: "UPDATE", schema: "public", table: "group_progress",
          filter: `group_ref=eq.${ref}`,
        }, () => { loadFeedback().catch(() => {}); loadScores(); })
        .subscribe(status => { subscribed = status === "SUBSCRIBED"; });
    };
    const knownRef = getGroupRef(session.sessionId, groupName);
    if (knownRef) openChannel(knownRef);
    else dbEnsureGroupRef(session.sessionId, groupName).then(openChannel).catch(() => {});

    return () => {
      cancelled = true;
      stopFeedback(); stopStatus();
      if (channel) supabase.removeChannel(channel);
    };
  }, [asleep]);
  const pendingDelegations = delegations.filter(d => !d.answer).length;
  const serverTrouble = useServerTrouble();
  // Ingen omladdning medan ett överklagande, en Burning Question eller ett
  // svar till läraren är öppet (kryssrutor fångas inte av textfältskollen).
  useVersionCheck(() => showModal == null, asleep);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const exit = onExit || onHome;
  // Enheten glömmer gruppen bara när tRAT:en har gruppkoder: då kommer man
  // tillbaka med koden. Utan gruppkoder är enhetens nyckel enda vägen in igen,
  // så den behålls (annars vore gruppen utelåst för gott).
  const hasGroupCodes = !!session.config?.has_group_codes;
  function leave() {
    if (hasGroupCodes) forgetGroup(session.sessionId, groupName);
    exit();
  }

  if (isLocked) return <SessionClosed onExit={exit} score={{ total, max }} lang={lang} G={G} css={css} />;

  function handleCelebrate() {
    const percentage = (total / max) * 100;
    if (percentage === 100) celebratePerfect();
    else if (percentage >= 75) celebrateGood();
    else celebrateStandard();
  }
  
  return (
    <div style={css.page}>
      <ServerTroubleBanner trouble={serverTrouble} audience="student" lang={lang} G={G} />
      <SleepBanner asleep={asleep && !serverTrouble} lang={lang} G={G} />
      <AppHeader lang={lang} setLang={setLang} onHome={onHome} subtitle={groupName} theme={theme} setTheme={setTheme} onHelp={onHelp} G={G} css={css}>
        <button style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 14 }} onClick={onBack}>{t.back}</button>
      </AppHeader>
      <div style={{ display: "flex", justifyContent: "center", padding: "40px 20px" }}>
        <div style={css.card}>
          {/* Grupp och status */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: G.muted }}>{groupName}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 500, color: G.correct, background: G.correct + "18", borderRadius: 999, padding: "3px 10px" }}>
              {doneCount === session.numQ && <Check size={12} aria-hidden="true" />}
              {doneCount === session.numQ
                ? (lang === "sv" ? `Klar · ${session.numQ} frågor` : `Done · ${session.numQ} questions`)
                : (lang === "sv" ? `${doneCount} av ${session.numQ} frågor` : `${doneCount} of ${session.numQ} questions`)}
            </span>
          </div>

          {/* Poäng */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ ...css.heading, fontSize: 60, lineHeight: 1, color: total / max >= 0.7 ? G.correct : scoreColor(total / max, G) }}>{total}</span>
            <span style={{ fontSize: 17, color: G.muted }}>{t.outOf} {max} {t.points}</span>
            <span style={{ marginLeft: "auto", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 18, fontWeight: 700, color: G.text }}>{Math.round((total / max) * 100)} %</span>
          </div>

          {/* Fördelning per försök: en stapel i stället för fyra rutor */}
          <div role="img" aria-label={ATTEMPT_BARS.map(b => `${counts[b.pts] || 0} ${b.label(lang)}`).join(", ")}
            style={{ display: "flex", gap: 2, height: 8, borderRadius: 4, overflow: "hidden", margin: "16px 0 8px", background: G.surface2 }}>
            {ATTEMPT_BARS.map(b => (counts[b.pts] || 0) > 0 && (
              <div key={b.pts} style={{ flex: counts[b.pts], background: b.color(G) }} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: G.muted, marginBottom: finishWarning ? 16 : 24 }}>
            {ATTEMPT_BARS.map(b => (
              <span key={b.pts} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: b.color(G) }} />
                {counts[b.pts] || 0} {b.label(lang)}
              </span>
            ))}
          </div>

          {decisionNotices.map(a => (
            <div key={a._key} role="status" style={{ display: "flex", alignItems: "flex-start", gap: 10, background: (a.status === "approved" ? G.correct : G.muted) + "12", border: `1px solid ${(a.status === "approved" ? G.correct : G.border)}55`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 14, color: G.text, lineHeight: 1.5 }}>
              <Scale size={16} color={a.status === "approved" ? G.correct : G.muted} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ flex: 1 }}>{appealDecisionText(a, lang)}</span>
              <button onClick={() => setDecisionNotices(p => p.filter(x => x._key !== a._key))} aria-label={lang === "sv" ? "Stäng" : "Close"}
                style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 2px" }}>&times;</button>
            </div>
          ))}

          {finishWarning && (
            <div role="alert" style={{ background: G.red + "18", border: `1px solid ${G.red}55`, borderRadius: 10, padding: "10px 14px", marginBottom: 20, fontSize: 13, color: G.text }}>
              {lang === "sv"
                ? "Avslutet kunde inte sparas hos servern — ladda om sidan när ni har täckning. Era svar är sparade."
                : "Finishing could not be saved on the server — reload when you have a connection. Your answers are saved."}
            </div>
          )}


          {/* En annan grupp har skickat en fråga till er */}
          {answerNotices.map(d => (
            <div key={d._key} role="status" style={{ display: "flex", alignItems: "center", gap: 8, background: G.correct + "12", border: `1px solid ${G.correct}55`, borderRadius: 12, marginBottom: 12 }}>
              <button onClick={() => { setModalQ(Math.max(0, (d.iFATQuestion || 1) - 1)); setShowModal("bq"); setAnswerNotices(p => p.filter(x => x._key !== d._key)); }}
                style={{ flex: 1, background: "none", border: "none", padding: "12px 0 12px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, textAlign: "left", fontFamily: "'DM Sans', sans-serif" }}>
                <MessageCircleQuestion size={20} color={G.correct} aria-hidden="true" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: G.text, fontSize: 14, fontWeight: 600 }}>
                    {lang === 'sv' ? `${d.toGroup} har svarat på er burning question` : `${d.toGroup} answered your burning question`}
                  </div>
                  <div style={{ color: G.muted, fontSize: 12, marginTop: 2 }}>{lang === 'sv' ? `Fråga ${d.iFATQuestion} · tryck för att läsa svaret` : `Question ${d.iFATQuestion} · tap to read the answer`}</div>
                </div>
                <ChevronRight size={18} color={G.muted} aria-hidden="true" />
              </button>
              <button onClick={() => setAnswerNotices(p => p.filter(x => x._key !== d._key))} aria-label={lang === "sv" ? "Stäng" : "Close"}
                style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 12px" }}>&times;</button>
            </div>
          ))}

          {pendingDelegations > 0 && (
            <button onClick={() => setShowModal("delegations")} style={{ width: "100%", background: G.gold + "12", border: `1px solid ${G.gold}66`, borderRadius: 12, padding: "12px 14px", marginBottom: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, textAlign: "left", fontFamily: "'DM Sans', sans-serif" }}>
              <MessageCircleQuestion size={20} color={G.gold} aria-hidden="true" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: G.text, fontSize: 14, fontWeight: 600 }}>{lang === 'sv' ? 'Ni har fått en fråga' : 'Your group received a question'}</div>
                <div style={{ color: G.muted, fontSize: 12, marginTop: 2 }}>{lang === 'sv' ? `${pendingDelegations} burning question väntar på ert svar` : `${pendingDelegations} burning question${pendingDelegations > 1 ? 's' : ''} waiting for your answer`}</div>
              </div>
              <ChevronRight size={18} color={G.muted} aria-hidden="true" />
            </button>
          )}

          <button onClick={() => setShowModal("myscratch")} style={{ ...css.btnPrimary, width: "100%" }}>{t.showGroupAnswers}</button>

          {/* Appeals och burning questions som en lugn lista */}
          <div style={{ border: `1px solid ${G.border}`, borderRadius: 12, marginTop: 12, overflow: "hidden" }}>
            {[
              { id: "appeal", Icon: Scale, label: t.appeals, badge: 0 },
              { id: "bq", Icon: MessageCircleQuestion, label: t.burningQuestions, badge: pendingDelegations },
            ].map(({ id, Icon, label, badge }, i) => (
              <button key={id} onClick={() => setShowModal(id)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: "transparent", border: "none", borderTop: i > 0 ? `1px solid ${G.border}` : "none", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans', sans-serif", color: G.text, transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <Icon size={20} color={G.muted} aria-hidden="true" />
                <span style={{ flex: 1, fontSize: 15, fontWeight: 500 }}>{label}</span>
                {badge > 0 && <span style={{ background: G.red, color: "#fff", borderRadius: 999, fontSize: 11, fontWeight: 600, padding: "1px 7px" }}>{badge}</span>}
                <ChevronRight size={18} color={G.muted} aria-hidden="true" />
              </button>
            ))}
          </div>

          {/* Sidfot: tillbaka, fira, spara */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${G.border}` }}>
            <button onClick={onBack} style={footerBtn(G, G.sub)}>{t.backToCards}</button>
            <div style={{ display: "flex", gap: 16 }}>
              <button onClick={handleCelebrate} style={footerBtn(G, G.gold)}><PartyPopper size={15} aria-hidden="true" />{lang === 'sv' ? 'Fira igen' : 'Celebrate again'}</button>
              <button onClick={() => openStudentResultSheet(session, groupName, states, lang)} style={footerBtn(G, G.sub)}><Download size={15} aria-hidden="true" />{lang === 'sv' ? 'Spara resultat' : 'Save results'}</button>
            </div>
          </div>
        </div>
      </div>
      {/* Klar: lämna tRAT:en, så att enheten (t.ex. en delad iPad) är redo för nästa grupp */}
      <div style={{ display: "flex", justifyContent: "center", padding: "0 20px 40px" }}>
        <button onClick={() => setConfirmLeave(true)} style={{ ...footerBtn(G, G.sub), border: `1px solid ${G.border}`, borderRadius: 10, padding: "10px 18px" }}>
          <LogOut size={15} aria-hidden="true" />{lang === 'sv' ? 'Lämna tRAT:en' : 'Leave the tRAT'}
        </button>
      </div>
      {confirmLeave && (
        <ConfirmDialog
          text={lang === 'sv'
            ? (hasGroupCodes
              ? 'Lämna tRAT:en? Era svar är sparade. Vill ni tillbaka behöver ni sessionskoden och gruppkoden.'
              : 'Lämna tRAT:en? Era svar är sparade. Vill ni tillbaka skriver ni sessionskoden på den här enheten.')
            : (hasGroupCodes
              ? 'Leave the tRAT? Your answers are saved. To come back you need the session code and the group code.'
              : 'Leave the tRAT? Your answers are saved. To come back, enter the session code on this device.')}
          confirmLabel={lang === 'sv' ? 'Lämna' : 'Leave'}
          danger={false}
          onConfirm={leave}
          onCancel={() => setConfirmLeave(false)}
          lang={lang} G={G} css={css}
        />
      )}
      {showModal && <PostSessionModal session={liveSession} groupName={groupName} states={states} delegations={delegations} bqAnswers={bqAnswers} onClose={() => { setShowModal(null); setModalQ(0); }} initialTab={showModal} initialQ={modalQ} lang={lang} G={G} css={css} isLocked={isLocked} />}
    </div>
  );
}

export { PostSessionModal, GroupResults };
